/**
 * Framer-motion animation variants for the chat module.
 * Import from here — no ad-hoc transitions in components.
 * All variants respect prefers-reduced-motion via reducedMotion().
 */

import type { Variants, Transition } from 'framer-motion';

// ── Easing curves ────────────────────────────────────────────────────

export const ease = {
  outCubic:  [0.33, 1, 0.68, 1] as [number,number,number,number],
  inOut:     [0.4, 0, 0.2, 1]   as [number,number,number,number],
  spring:    { type: 'spring', stiffness: 320, damping: 28 } as Transition,
  springSoft:{ type: 'spring', stiffness: 240, damping: 32 } as Transition,
  elastic:   [0.68, -0.55, 0.27, 1.55] as [number,number,number,number],
} as const;

export const dur = { xs: 0.1, sm: 0.15, md: 0.2, lg: 0.25, xl: 0.3 } as const;

// ── Reduced-motion guard ─────────────────────────────────────────────

function reducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function rm<T extends object>(full: T, reduced: T): T {
  return reducedMotion() ? reduced : full;
}

// ── Message animations ───────────────────────────────────────────────

export const messageSentVariants: Variants = {
  hidden: rm(
    { y: 12, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      y: 0,
      opacity: 1,
      transition: { duration: dur.sm, ease: ease.outCubic },
    },
    {
      opacity: 1,
      transition: { duration: 0.08 },
    },
  ),
};

export const messageReceivedVariants: Variants = {
  hidden: rm(
    { x: -8, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      x: 0,
      opacity: 1,
      transition: ease.springSoft,
    },
    {
      opacity: 1,
      transition: { duration: 0.08 },
    },
  ),
};

export const avatarVariants: Variants = {
  hidden: rm({ scale: 0.8, opacity: 0 }, { opacity: 0 }),
  visible: rm(
    {
      scale: 1,
      opacity: 1,
      transition: { delay: 0.05, ...ease.spring },
    },
    { opacity: 1 },
  ),
};

// ── Typing indicator ─────────────────────────────────────────────────

export const typingDotVariants: Variants = {
  bounce: rm(
    {
      y: [0, -3, 0],
      transition: {
        duration: 0.6,
        ease: ease.elastic,
        repeat: Infinity,
        repeatType: 'loop',
      },
    },
    { y: 0 },
  ),
};

export const typingDotStagger = 0.12; // seconds between each dot

// ── Panel / sidebar ──────────────────────────────────────────────────

export const sidebarVariants: Variants = {
  open: rm(
    { width: 'var(--gc-sidebar-width)', opacity: 1, transition: { duration: dur.lg, ease: ease.inOut } },
    { opacity: 1 },
  ),
  closed: rm(
    { width: 0, opacity: 0, transition: { duration: dur.lg, ease: ease.inOut } },
    { opacity: 0 },
  ),
};

export const threadPanelVariants: Variants = {
  hidden: rm(
    { x: '100%', opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      x: 0,
      opacity: 1,
      transition: { duration: dur.xl, ease: ease.inOut },
    },
    { opacity: 1, transition: { duration: 0.08 } },
  ),
  exit: rm(
    { x: '100%', opacity: 0, transition: { duration: dur.lg, ease: ease.inOut } },
    { opacity: 0 },
  ),
};

// ── Channel switch ───────────────────────────────────────────────────

export const channelOutVariants: Variants = {
  exit: rm(
    { opacity: 0, transition: { duration: 0.05 } },
    { opacity: 0 },
  ),
};

export const channelInVariants: Variants = {
  hidden: rm(
    { opacity: 0, y: 8 },
    { opacity: 0 },
  ),
  visible: rm(
    { opacity: 1, y: 0, transition: { duration: dur.sm, ease: ease.outCubic } },
    { opacity: 1, transition: { duration: 0.08 } },
  ),
};

// ── Context menu ─────────────────────────────────────────────────────

export const contextMenuVariants: Variants = {
  hidden: rm(
    { scale: 0.95, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.12, ease: ease.outCubic },
    },
    { opacity: 1, transition: { duration: 0.08 } },
  ),
  exit: rm(
    { scale: 0.95, opacity: 0, transition: { duration: 0.08 } },
    { opacity: 0 },
  ),
};

// ── Modal ────────────────────────────────────────────────────────────

export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: dur.md } },
  exit:    { opacity: 0, transition: { duration: dur.sm } },
};

export const modalVariants: Variants = {
  hidden: rm(
    { scale: 0.96, y: 16, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      scale: 1,
      y: 0,
      opacity: 1,
      transition: { ...ease.spring, delay: 0.05 },
    },
    { opacity: 1, transition: { duration: 0.08 } },
  ),
  exit: rm(
    { scale: 0.96, y: 8, opacity: 0, transition: { duration: dur.sm } },
    { opacity: 0 },
  ),
};

// ── Hover action bar ─────────────────────────────────────────────────

export const actionBarVariants: Variants = {
  hidden: rm(
    { scale: 0.9, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      scale: 1,
      opacity: 1,
      transition: { duration: dur.xs, ease: ease.outCubic },
    },
    { opacity: 1, transition: { duration: 0.05 } },
  ),
};

// ── Reactions ────────────────────────────────────────────────────────

export const reactionAddVariants: Variants = {
  pop: rm(
    {
      scale: [1, 1.4, 1],
      transition: { duration: dur.md, ease: ease.elastic },
    },
    { scale: 1 },
  ),
};

export const reactionRemoveVariants: Variants = {
  shake: rm(
    {
      x: [0, -2, 2, -2, 0],
      transition: { duration: 0.18 },
    },
    { x: 0 },
  ),
};

// ── New message banner ───────────────────────────────────────────────

export const newMsgBannerVariants: Variants = {
  hidden: rm(
    { y: -40, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    { y: 0, opacity: 1, transition: { duration: dur.lg, ease: ease.outCubic } },
    { opacity: 1 },
  ),
  exit: rm(
    { y: -40, opacity: 0, height: 0, transition: { duration: dur.xl } },
    { opacity: 0 },
  ),
};

// ── Toast ────────────────────────────────────────────────────────────

export const toastVariants: Variants = {
  hidden: rm(
    { x: 80, opacity: 0 },
    { opacity: 0 },
  ),
  visible: rm(
    { x: 0, opacity: 1, transition: ease.springSoft },
    { opacity: 1 },
  ),
  exit: rm(
    { x: 80, opacity: 0, transition: { duration: dur.sm } },
    { opacity: 0 },
  ),
};

// ── Emoji picker / popover ───────────────────────────────────────────

export const popoverVariants: Variants = {
  hidden: rm(
    { scale: 0.95, opacity: 0, y: 4 },
    { opacity: 0 },
  ),
  visible: rm(
    {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { duration: 0.12, ease: ease.outCubic },
    },
    { opacity: 1 },
  ),
  exit: rm(
    { scale: 0.95, opacity: 0, y: 4, transition: { duration: 0.08 } },
    { opacity: 0 },
  ),
};

// ── DnD drag ghost ───────────────────────────────────────────────────

export const dragGhostStyle = {
  scale: 0.95,
  boxShadow: 'var(--gc-elev-3)',
  opacity: 0.9,
} as const;
